import { getEventDetails } from '@store/slices/eventSlice';
import { useSelector } from 'react-redux';
import {
  Container,
  ImageWrapper,
  LogoImage,
  ItemTitle,
  ItemWrapper,
  RightWrapper,
  ItemAbout,
  LeftWrapper,
  Badge,
  EventLogoWrapper,
  EventLogo,
  CategoryTitle,
  GroupContainer,
  DividerWrapper,
} from './styles';
import { useGetMergedGroupsData } from '@utils/apiHooks';
import { getImageSrc } from '@utils/fn';
import { useMemo, useState } from 'react';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import Button from '@components/Button';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useMobileWidth } from '@utils/hooks';
import PartnerWithUsSection from '../PartnerWithUsSection';
import { adminEventsRouteFormat } from '@constants/paths';
import { useNavigate } from 'react-router-dom';
import Divider from '@components/Divider';

const CATEGORIES: string[] = [
  'partner_elite',
  'partner_eventlead',
  'partner_competence',
  'partner_business',
  'partner_exhibitor',
  'partner_startups',
  'partner_media',
];

const categoryTranslationMap: Record<string, string> = {
  partner_elite: 'elite-partner',
  partner_eventlead: 'event-lead-partner',
  partner_competence: 'competence-partner',
  partner_business: 'business-partner',
  partner_exhibitor: 'exhibitor-partner',
  partner_startups: 'startups-partner',
  partner_media: 'media-partner',
};

interface BaseItem {
  id: string;
  category: string;
  name: string;
  [key: string]: any;
}

interface GroupedItem {
  category: string;
  title?: string;
  items: BaseItem[];
}

const GroupItem = ({
  item,
  event,
}: {
  item: BaseItem;
  event: PublicEventDetailsResponse | undefined;
  currentEventSlug?: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const navigate = useNavigate();

  return (
    <article>
      <ItemWrapper key={item.id}>
        <Badge color={event?.design?.themeColor}>
          {t(categoryTranslationMap[item?.category || ''], { lng: 'en' })}
        </Badge>

        <LeftWrapper color={event?.design?.themeColor}>
          <ImageWrapper>
            <LogoImage
              alt={item?.name}
              src={getImageSrc(item?.company?.logo || '')}
            />
          </ImageWrapper>
        </LeftWrapper>

        <RightWrapper color={event?.design?.themeColor}>
          <EventLogoWrapper>
            {/* <EventLogo
              alt={event?.name}
              src={getImageSrc(event?.images?.logo)}
            /> */}
          </EventLogoWrapper>
          <ItemTitle>{item.name}</ItemTitle>
          <ItemAbout>
            <div dangerouslySetInnerHTML={{ __html: item?.company?.about }} />
          </ItemAbout>

          <Button
            title={t('more-information')}
            backgroundColor={event?.design.themeColor}
            hoverBackgroundColor={theme.color.mobilityGrey}
            textColor={theme.color.white}
            borderRadius={theme.borderRadius.radius10}
            padding="0.8rem 4.5rem"
            width={isMobile ? '100%' : 'fit-content'}
            onClick={() => {
              const sourceQuery =
                typeof item.event === 'string' && item.event.length > 0
                  ? `?sourceEvent=${item.event}`
                  : '';
              navigate(
                adminEventsRouteFormat(
                  event?.slug ?? '',
                  `${t('partners-url')}/${item.id}${sourceQuery}`
                )
              );
            }}
          />
        </RightWrapper>
      </ItemWrapper>
    </article>
  );
};

const PartnersGroupsSection = () => {
  const { t } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  const eventIds = useMemo(
    () => (sluggedEvent?.id ? [sluggedEvent.id] : []),
    [sluggedEvent?.id]
  );
  const rawGroups =
    useGetMergedGroupsData({
      eventIds,
      categories: CATEGORIES,
    }).data || [];

  const groups = useMemo(
    () => (Array.isArray(rawGroups) ? rawGroups : []),
    [rawGroups]
  );

  const groupedItemsByCategory = useMemo<GroupedItem[]>(() => {
    const grouped = groups.reduce<Record<string, GroupedItem>>((acc, item) => {
      const { category } = item;
      // Added groupName for title
      if (!acc[category]) {
        acc[category] = {
          category,
          title: item?.groupName,
          items: [],
        };
      }
      acc[category].items.push(item);

      return acc;
    }, {});

    const sorted = Object.values(grouped);

    return sorted.sort((a, b) =>
      a.category === 'partner_elite'
        ? -1
        : b.category === 'partner_elite'
          ? 1
          : 0
    );
  }, [groups]);

  const renderPartnerWithUs = (category: string) => {
    if (['partner_competence', 'partner_startups'].includes(category)) {
      return <PartnerWithUsSection width="100vw" />;
    }

    return null;
  };

  return (
    <Container>
      {groupedItemsByCategory?.map((groupedItem: GroupedItem) => {
        return (
          <GroupContainer key={`category_${groupedItem.category}`}>
            {renderPartnerWithUs(groupedItem.category)}
            <CategoryTitle color={sluggedEvent?.design?.headerFontColor}>
              {groupedItem.title ||
                t(categoryTranslationMap[groupedItem?.category || ''], {
                  lng: 'en',
                })}
              <DividerWrapper>
                <Divider
                  width={'16rem'}
                  height="0.4rem"
                  backgroundColor={sluggedEvent?.design?.themeColor}
                />
              </DividerWrapper>
            </CategoryTitle>

            {groupedItem?.items?.map((item: BaseItem) => {
              return (
                <GroupItem
                  key={item.id}
                  item={item}
                  event={sluggedEvent}
                  currentEventSlug={sluggedEvent?.slug}
                />
              );
            })}
          </GroupContainer>
        );
      })}
    </Container>
  );
};

export default PartnersGroupsSection;
